import React, { useEffect, useState } from "react";
import {
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Checkbox,
  CircularProgress,
  TableContainer,
  Paper,
  TablePagination,
  Select,
  MenuItem,
  Alert,
  Button,
  Stack,
  Snackbar,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useSnackbar } from 'notistack';
import TableChartIcon from "@mui/icons-material/TableChart";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import axios from "axios";
import * as XLSX from "xlsx";

interface Advantage {
  id: number;
  type: string;
}

interface Expense {
  id: number;
  date: string;
  amountAccepted: number;
  isSelected: boolean;
  advantage: Advantage;
  status: string;
}

interface Contract {
  id: number;
  registrationNumber: string;
  title: string;
}

interface Employee {
  id: number;
  firstName: string;
  lastName: string;
  contract: Contract;
  expenses: Expense[];
}

const ExpensesByEmployeeView: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState("PENDING_EMPLOYER_APPROVAL");
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  // Import modal state
  const [openImport, setOpenImport] = useState(false);
  const [importing, setImporting] = useState(false);

  const API_BASE = "https://staging-third-party-apis.nabil5332.workers.dev/api/v1";

  // --- Fetch grouped expenses ---
  const fetchExpensesByEmployee = async () => {
    try {
      setLoading(true);
      const apiKey = localStorage.getItem("apiKey");
      if (!apiKey) {
        alert("Aucune clé API trouvée. Créez-en une dans la page Manage.");
        return;
      }

      const res = await axios.get(`${API_BASE}/expenses/by-employee`, {
        headers: { Authorization: `Bearer ${apiKey}` },
        params: { page: page + 1, pageSize: rowsPerPage, statuses: statusFilter },
      });

      const filteredEmployees = (res.data.employees || []).filter(
        (emp: Employee) => emp.expenses && emp.expenses.length > 0
      );

      setEmployees(filteredEmployees);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error("❌ Erreur lors du chargement :", err);
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  // --- Handle single expense selection (PUT) ---
  const handleSelectExpense = async (expenseId: number, select: boolean) => {
    if (statusFilter !== "PENDING_EMPLOYER_APPROVAL") return;

    try {
      const apiKey = localStorage.getItem("apiKey");
      if (!apiKey) {
        alert("Aucune clé API trouvée.");
        return;
      }

      await axios.put(
        `${API_BASE}/expenses/selection`,
        { updates: [{ expenseId, select }] },
        { headers: { Authorization: `Bearer ${apiKey}` } }
      );

      setEmployees((prev) =>
        prev.map((emp) => ({
          ...emp,
          expenses: emp.expenses.map((exp) =>
            exp.id === expenseId ? { ...exp, isSelected: select } : exp
          ),
        }))
      );
    } catch (err) {
      console.error("❌ Erreur de sélection :", err);
      alert("Erreur lors de la mise à jour de la sélection.");
    }
  };

  // --- Toggle all expenses for an employee ---
  const handleToggleAllExpenses = async (employeeId: number, selectAll: boolean) => {
    if (statusFilter !== "PENDING_EMPLOYER_APPROVAL") return;

    const employee = employees.find((e) => e.id === employeeId);
    if (!employee) return;

    const updates = employee.expenses.map((exp) => ({ expenseId: exp.id, select: selectAll }));

    try {
      const apiKey = localStorage.getItem("apiKey");
      if (!apiKey) return;

      await axios.put(`${API_BASE}/expenses/selection`, { updates }, { headers: { Authorization: `Bearer ${apiKey}` } });

      setEmployees((prev) =>
        prev.map((emp) =>
          emp.id === employeeId
            ? { ...emp, expenses: emp.expenses.map((exp) => ({ ...exp, isSelected: selectAll })) }
            : emp
        )
      );
    } catch (err) {
      console.error("❌ Erreur tout cocher :", err);
    }
  };

  // --- Confirm (create invoice) ---
  const handleConfirm = async () => {
    try {
      setConfirmLoading(true);
      const apiKey = localStorage.getItem("apiKey");
      if (!apiKey) {
        alert("Aucune clé API trouvée.");
        return;
      }

      const idempotencyKey = crypto.randomUUID();

      const res = await axios.post(
        `${API_BASE}/invoices`,
        {},
        {
          headers: { Authorization: `Bearer ${apiKey}`, "Idempotency-Key": idempotencyKey },
        }
      );

      const { invoice } = res.data;
      setSnackbarMessage(`✅ Facture générée avec succès : #${invoice?.uniqueNumber ?? invoice?.id ?? "?"}`);
      fetchExpensesByEmployee();
    } catch (err: any) {
      console.error("❌ Erreur de génération de facture :", err);
      setSnackbarMessage("❌ Erreur lors de la création de la facture.");
    } finally {
      setConfirmLoading(false);
    }
  };

  // --- Export Excel ---
  const handleExportExcel = async () => {
    try {
      const apiKey = localStorage.getItem("apiKey");
      if (!apiKey) return;

      const res = await axios.get(`${API_BASE}/expenses/excel-format`, {
        headers: { Authorization: `Bearer ${apiKey}` },
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `expenses.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("❌ Erreur export Excel :", err);
      setSnackbarMessage("❌ Erreur lors de l'export Excel.");
    }
  };

  useEffect(() => {
    fetchExpensesByEmployee();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, statusFilter]);

  const handleChangePage = (event: unknown, newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // ------------------------------
  // --- IMPORT EXCEL HANDLER -----
  // ------------------------------
  const handleOpenImport = () => setOpenImport(true);
  const handleCloseImport = () => {
    if (!importing) setOpenImport(false);
  };



  const handleFileUpload = async (file: File) => {
    if (!file) return;

    try {
      setImporting(true);

      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: "array" });

      console.log("Feuilles Excel:", workbook.SheetNames);

      // Lire la 2ᵉ feuille (Dépenses)
      const sheetName = workbook.SheetNames[1];
      const sheet = workbook.Sheets[sheetName];
      const rows: Record<string, any>[] = XLSX.utils.sheet_to_json(sheet);

      console.log("=== KEYS OF ROW(0) ===", Object.keys(rows[0]));

      const updatedEmployees = employees.map(emp => ({
        ...emp,
        expenses: emp.expenses.map(exp => ({ ...exp }))
      }));

      const expenseUpdates: { expenseId: number; select: boolean }[] = [];

      rows.forEach((row, rowIndex) => {

        const cleanedRow: Record<string, any> = {};
        Object.keys(row).forEach(k => {
          const cleanKey = k.replace(/["\n\r]/g, " ").trim().replace(/\s+/g, "_");
          cleanedRow[cleanKey] = row[k];
        });

        const matricule = (cleanedRow.Matricule ?? "").toString().trim();
        const expenseId = Number(cleanedRow.Id);

        const accordKey = Object.keys(row).find((k) =>
          k.replace(/\s+/g, " ").toLowerCase().includes("accord remboursement")
        );
        const accordRaw = accordKey ? row[accordKey] : "";
        const accord = String(accordRaw || "").trim().toLowerCase();

        if (!matricule || !expenseId) return;

        const employee = updatedEmployees.find(emp =>
          emp.contract?.registrationNumber?.toString() === matricule
        );
        if (!employee) return;

        employee.expenses = employee.expenses.map(exp => {
          if (exp.id === expenseId && exp.status === "PENDING_EMPLOYER_APPROVAL") {
            const shouldCheck = accord === "ok";
            expenseUpdates.push({ expenseId: exp.id, select: shouldCheck });
            return { ...exp, isSelected: shouldCheck };
          }
          return exp;
        });
      });

      console.log("=== FINAL EXPENSE UPDATES ===", expenseUpdates);


      for (const update of expenseUpdates) {
        await handleSelectExpense(update.expenseId, update.select);
      }
      await fetchExpensesByEmployee();

      enqueueSnackbar('Import effectué et cases à cocher mises à jour !', { variant: 'success' });

    } catch (err) {
      console.error("❌ Erreur lors de l'import Excel :", err);
      enqueueSnackbar('Erreur lors de la lecture du fichier Excel !', { variant: 'error' });
    } finally {
      setImporting(false);
    }
  };

  // --- Filter employees by search ---
  const filteredEmployees: Employee[] = employees.filter((emp) => {
    const fullName = `${emp.firstName} ${emp.lastName}`.toLowerCase();
    return fullName.includes(search.toLowerCase());
  });





  return (
    <Box p={4}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" sx={{ color: "#714291" }}>
          Expenses Grouped by Employee
        </Typography>

        <Stack direction="row" spacing={1}>
          <Tooltip title="Importer depuis Excel">
            <Button
              variant="outlined"
              startIcon={<UploadFileIcon />}
              onClick={handleOpenImport}
              sx={{ textTransform: "none" }}
            >
              Import
            </Button>
          </Tooltip>

          <Tooltip title="Exporter en Excel">
            <IconButton color="primary" onClick={handleExportExcel}>
              <FileDownloadIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Filters */}
      <Box mb={2} display="flex" gap={2}>
        <Select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(0);
          }}
        >
          <MenuItem value="PENDING_EMPLOYER_APPROVAL">Pending Employer Approval</MenuItem>
          <MenuItem value="PENDING_PAYMENT">Pending Payment</MenuItem>
        </Select>

        <input
          type="text"
          placeholder="Rechercher un employé..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            padding: "10px",
            borderRadius: "6px",
            border: "1px solid #ccc",
            flex: 1,
          }}
        />
      </Box>

      {/* Loading */}
      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress size={40} />
        </Box>
      ) : filteredEmployees.length === 0 ? (
        <Alert severity="info">Aucun employé trouvé.</Alert>
      ) : (
        <>
          {filteredEmployees.map((emp: Employee) => {
            const totalAmount = emp.expenses.reduce(
              (sum: number, exp: Expense) => sum + (exp.amountAccepted || 0),
              0
            );

            const selectedAmount = emp.expenses
              .filter((exp: Expense) => exp.isSelected)
              .reduce((sum: number, exp: Expense) => sum + (exp.amountAccepted || 0), 0);

            const allSelected = emp.expenses.every((e: Expense) => e.isSelected);

            return (
              <Accordion key={emp.id} sx={{ mb: 2, borderRadius: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography sx={{ fontWeight: 600, color: "#714291" }}>
                    {emp.firstName} {emp.lastName} —{" "}
                    {emp.contract?.registrationNumber || "Matricule inconnu"} • Total sélectionné :{" "}
                    <span style={{ color: "#1976d2", fontWeight: 600 }}>
                      {selectedAmount.toFixed(2)} / {totalAmount.toFixed(2)} MAD
                    </span>
                  </Typography>
                </AccordionSummary>

                <AccordionDetails>
                  {/* --- CONTENU DES DÉPENSES ICI (table, checkboxes, etc.) --- */}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </>
      )}

    
      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[50,100,200]}
      />

    
      <Snackbar
        open={!!snackbarMessage}
        autoHideDuration={4000}
        onClose={() => setSnackbarMessage(null)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />

      {/* Import Dialog */}
      <Dialog fullWidth maxWidth="sm" open={openImport} onClose={handleCloseImport}>
        <DialogTitle>Importer les dépenses depuis Excel</DialogTitle>

        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Sélectionnez un fichier Excel (.xlsx)
          </Typography>

          <input
            id="excel-file-input"
            type="file"
            accept=".xlsx, .xls"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFileUpload(file);
            }}
            disabled={importing}
            style={{ marginTop: 8 }}
          />

          {importing && (
            <Box display="flex" justifyContent="center" mt={2}>
              <CircularProgress />
            </Box>
          )}

          <Alert severity="info" sx={{ mt: 2 }}>
            Le mapping attendu : <strong>Id</strong>, <strong>Matricule</strong>, et “Accord Remboursement”
          </Alert>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseImport} disabled={importing}>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

};

export default ExpensesByEmployeeView;
